import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Chip,
    CircularProgress,
    Alert,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { PortfolioLpoa } from '../../clients/graphql/pages/lpoa-queries';
import { 
    getLpoaStatus, 
    isDocumentCreated, 
    isRejected, 
    isRevoked,
} from '../../utils/lpoaStatus';
import { LpoaDetailsModal } from './LpoaDetailsModal';

interface LpoaSummaryCardProps {
    userKey: string | number;
    lpoaData: PortfolioLpoa[];
    loading?: boolean;
    error?: any;
}

interface LpoaSummaryCounts {
    pending: number;
    signed: number;
    active: number;
    rejected: number;
    revoked: number;
    total: number;
}

export const LpoaSummaryCard: React.FC<LpoaSummaryCardProps> = ({ 
    userKey, 
    lpoaData, 
    loading, 
    error 
}) => {
    const [openDialog, setOpenDialog] = useState(false);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="warning" sx={{ margin: '.75rem' }}>
                {error.message}
            </Alert>
        );
    }

    if (!lpoaData || lpoaData.length === 0) {
        return (
            <Box sx={{
                border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid lightgray',
                borderRadius: '8px',
                paddingLeft: '1rem',
                marginLeft: '.75rem',
                marginRight: '.75rem',
                marginBottom: '1rem',
                padding: '1rem',
                textAlign: 'center'
            }}>
                <Typography fontWeight="lighter">No LPOA records found</Typography>
            </Box>
        );
    }

    // Calculate per-portfolio status counts
    const calculateSummaryCounts = (): LpoaSummaryCounts => {
        const counts: LpoaSummaryCounts = {
            pending: 0,
            signed: 0,
            active: 0,
            rejected: 0,
            revoked: 0,
            total: lpoaData.length
        };

        lpoaData.forEach(lpoa => {
            const status = getLpoaStatus(lpoa);
            
            if (isRejected(lpoa)) {
                counts.rejected++;
            } else if (isRevoked(lpoa)) {
                counts.revoked++;
            } else if (status.isActive) {
                counts.active++;
            } else if (status.isSigned) {
                counts.signed++;
            } else if (isDocumentCreated(lpoa)) {
                counts.pending++;
            } else {
                counts.pending++; // Default to pending for empty records
            }
        });

        return counts;
    };

    const summaryCounts = calculateSummaryCounts();

    const getStatusChip = (label: string, count: number, color: string) => (
        <Chip
            label={`${label}: ${count}`}
            size="small"
            sx={{
                backgroundColor: color,
                color: 'white',
                fontWeight: 600,
                fontSize: '0.75rem',
                margin: '2px',
            }}
        />
    );

    return (
        <Box sx={{
            border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid lightgray',
            borderRadius: '8px',
            paddingLeft: '1rem',
            marginLeft: '.75rem',
            marginRight: '.75rem',
            marginBottom: '1rem',
            padding: '1rem',
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    LPOA Summary
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => setOpenDialog(true)}
                    sx={{ 
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        minWidth: 'auto',
                        px: 1.5,
                        py: 0.5
                    }}
                >
                    View Details
                </Button>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Total Portfolios: {summaryCounts.total}
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {summaryCounts.active > 0 && getStatusChip('Active', summaryCounts.active, '#4caf50')}
                {summaryCounts.signed > 0 && getStatusChip('Signed', summaryCounts.signed, '#2196f3')}
                {summaryCounts.pending > 0 && getStatusChip('Pending', summaryCounts.pending, '#ff9800')}
                {summaryCounts.rejected > 0 && getStatusChip('Rejected', summaryCounts.rejected, '#f44336')}
                {summaryCounts.revoked > 0 && getStatusChip('Revoked', summaryCounts.revoked, '#9e9e9e')}
            </Box>

            <LpoaDetailsModal
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                lpoaData={lpoaData}
                userKey={userKey}
            />
        </Box>
    );
};